import { useState } from "react";
import { Phone, MessageCircle, Tag, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function DirectContact({ hotel, rooms, variant = "banner" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const tmrw = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    check_in: today, check_out: tmrw, guests: 2,
    room_id: rooms?.[0]?.id || "",
    message: "Hi, I'd like to enquire about a custom price for these dates.",
    traveler_phone: "",
  });

  const submit = async () => {
    if (!user) {
      toast.error("Please log in to send an inquiry");
      navigate("/login");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        hotel_id: hotel.id, room_id: form.room_id || null,
        check_in: form.check_in, check_out: form.check_out,
        guests: Number(form.guests), message: form.message,
        traveler_phone: form.traveler_phone,
      });
      toast.success("Inquiry sent! The host will respond with a custom price.");
      setOpen(false);
      navigate("/inquiries");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const sidebar = variant === "sidebar";
  const fw = sidebar ? "w-full justify-center" : "";

  const actions = (
    <div className={sidebar ? "flex flex-col gap-2" : "flex flex-wrap gap-2"}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="request-custom-price-btn" className={`rounded-xl bg-[#222] hover:bg-black text-white ${fw}`}>
            <Tag className="w-4 h-4 mr-1" /> Request custom price
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request a custom price</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs mb-1 block">Check in</Label>
                <Input data-testid="inquiry-checkin" type="date" value={form.check_in} min={today} onChange={(e) => setForm({ ...form, check_in: e.target.value })} className="rounded-xl" />
              </div>
              <div><Label className="text-xs mb-1 block">Check out</Label>
                <Input data-testid="inquiry-checkout" type="date" value={form.check_out} min={form.check_in} onChange={(e) => setForm({ ...form, check_out: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs mb-1 block">Guests</Label>
                <Input type="number" min={1} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="rounded-xl" />
              </div>
              <div><Label className="text-xs mb-1 block">Your phone</Label>
                <Input data-testid="inquiry-phone" placeholder="+91..." value={form.traveler_phone} onChange={(e) => setForm({ ...form, traveler_phone: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            {rooms?.length > 0 && (
              <div>
                <Label className="text-xs mb-1 block">Preferred room (optional)</Label>
                <select value={form.room_id} onChange={(e) => setForm({ ...form, room_id: e.target.value })} className="w-full border border-[#DDDDDD] rounded-xl px-3 py-2 text-sm bg-white">
                  <option value="">Any room</option>
                  {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label className="text-xs mb-1 block">Message to host</Label>
              <Textarea data-testid="inquiry-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="send-inquiry-btn" onClick={submit} disabled={submitting} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white w-full">
              {submitting ? "Sending..." : "Send inquiry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (sidebar) return actions;

  return (
    <div className="bg-gradient-to-r from-[#FFF2F4] to-[#FFF8E5] border border-[#FFE0E6] rounded-2xl p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FF385C] flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-display font-semibold text-[#222]">Connect directly with the host</p>
          <p className="text-sm text-[#555]">Skip the middleman — request a custom price directly from the host.</p>
        </div>
      </div>
      {actions}
    </div>
  );
}
